import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useIsCohortAnalysisCheck from '../../../../utils/routes/useIsCohortAnalysisCheck';
import * as BuilderUI from '../../../../components/Builder/BuilderUI';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';
import useBuilderSelector from '../../../../redux/selectorHooks/useBuilderSelector';
import useDefaultBuilderSchemaId from '../../../Formulas/utils/useDefaultBuilderSchemaId';
import { ContentLoader } from '../../../../components/Loaders';
import {
  populateAnalysisAndConfigThunk,
  populateNamedCohortThunk,
  populatePlainBuilderThunk,
} from '../../../../redux/slices/oldBuilder/populateThunks';
import { useAppDispatch } from '../../../../redux/hooks';
import CohortBuilder from './CohortBuilder';
import { useBuilderContext } from '../../../../components/Builder/BuilderContext';

const queryType = QueryType.CohortSample;

const CohortBuilderController = () => {
  const dispatch = useAppDispatch();
  const urlParams = useParams();
  const builderId = urlParams?.builderId;
  const isCohortAnalysisPage = useIsCohortAnalysisCheck();

  const { consumeJustExecuted } = useBuilderContext();

  const builderInitialized = useBuilderSelector('builderInitialized');
  const builderQueryType = useBuilderSelector('queryType');
  const defaultBuilderSchemaId = useDefaultBuilderSchemaId();

  // Run specific thunk with query calls to guarantee a proper Builder view
  useEffect(() => {
    // FEATURE: avoid Builder re-render on execute
    if (consumeJustExecuted()) {
      return;
    }

    if (builderId) {
      if (isCohortAnalysisPage) {
        // Cohort Analysis (report)
        dispatch(populateAnalysisAndConfigThunk({ analysisId: builderId }));
      } else {
        // Named Cohort definition
        dispatch(populateNamedCohortThunk({ namedCohortId: builderId }));
      }
    } else {
      // Plain(empty) builder definition with optional cached data
      dispatch(
        populatePlainBuilderThunk({ queryType, defaultBuilderSchemaId }),
      );
    }
  }, [
    builderId,
    consumeJustExecuted,
    defaultBuilderSchemaId,
    dispatch,
    isCohortAnalysisPage,
  ]);

  // For unknown reason we have a route lifecycle issue. When navigating between
  // report pages, like from Query to Funnel, component renders one more time
  // immediately after switch to a new report page. And we run into assertCondition
  // because of incorrect queryType.
  // builderQueryType !== queryType check is a temp solution
  // Similar hack exists in other places, tagged with: routing_issue_hack_2025-02-10
  if (!builderInitialized || builderQueryType !== queryType) {
    return (
      <BuilderUI.Container>
        <ContentLoader noBorders={true} />
      </BuilderUI.Container>
    );
  }

  return (
    <BuilderUI.Container>
      <CohortBuilder />
    </BuilderUI.Container>
  );
};

export default CohortBuilderController;
